import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faNoteSticky, faUserGroup, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import Avatar from './Avatar';
import MeetingRecordModal from './meeting/MeetingRecordModal';
import { timeText } from './meeting/meetingHelpers';

/**
 * The Meetings tab of the Task Report's day panel (ADMIN and HR): every meeting
 * that started that day, who ran it and who came, with the full record (chat,
 * notes, attendance) one click away.
 */

const STATUS_LABEL = { scheduled: 'Scheduled', live: 'Live', ended: 'Ended', cancelled: 'Cancelled' };

// A meeting nobody closed stays "scheduled" on the server: say what actually happened.
const statusOf = (m) => {
    if (m.status !== 'scheduled' || new Date(m.endsAt) > new Date()) return m.status;
    return m.attended.length ? 'ended' : 'not-held';
};
STATUS_LABEL['not-held'] = 'Not held';

const minutesText = (seconds) => {
    const total = Math.round(seconds / 60);
    if (total < 1) return '<1 min';
    const hours = Math.floor(total / 60);
    return hours ? `${hours}h ${total % 60}m` : `${total}m`;
};

const TaskReportMeetings = ({ meetings }) => {
    const [record, setRecord] = useState(null);
    const [opening, setOpening] = useState('');

    const open = async (meeting) => {
        setOpening(meeting._id);
        try {
            const res = await api.get(`/meetings/${meeting._id}/record`);
            setRecord(res.data);
        } catch (err) {
            Swal.fire('Could not open', err.response?.data?.message || 'The meeting record could not be loaded.', 'error');
        } finally {
            setOpening('');
        }
    };

    if (meetings.length === 0) return <p className="trc-empty">No meetings this day.</p>;

    return (
        <>
            <div className="trc-items">
                {meetings.map((m) => {
                    const status = statusOf(m);
                    const hasRecord = m.hasRoom && m.kind === 'meeting' && m.status !== 'cancelled';
                    return (
                        <div key={m._id} className="trc-meeting">
                            <div className="trc-meeting-head">
                                <span className="trc-item-title">{m.title}</span>
                                <span className="trc-item-type">{m.kind}</span>
                                <span className={`trc-meeting-status is-${status}`}>{STATUS_LABEL[status] || status}</span>
                            </div>
                            <div className="trc-meeting-meta">
                                <span>{timeText(m.startsAt)} – {timeText(m.endsAt)}</span>
                                {m.organizer && (
                                    <span className="trc-meeting-by">
                                        <Avatar name={m.organizer.name} profilePic={m.organizer.profilePic} className="assignee-avatar tiny" title={m.organizer.name} />
                                        Created by {m.organizer.name}
                                    </span>
                                )}
                                {m.project && <span><FontAwesomeIcon icon={faFolderOpen} /> {m.project.name}</span>}
                            </div>
                            <div className="trc-meeting-meta">
                                <span title="Invited"><FontAwesomeIcon icon={faUserGroup} /> {m.invited} invited, {m.attended.length} joined</span>
                                <span title="Chat messages"><FontAwesomeIcon icon={faComments} /> {m.chatMessages} chat</span>
                                <span title="Notes"><FontAwesomeIcon icon={faNoteSticky} /> {m.notes} note{m.notes === 1 ? '' : 's'}</span>
                                {hasRecord && (
                                    <button type="button" className="trc-meeting-open" onClick={() => open(m)} disabled={opening === m._id}>
                                        {opening === m._id ? 'Opening…' : 'Open record'}
                                    </button>
                                )}
                            </div>
                            {m.attended.length > 0 && (
                                <div className="trc-meeting-people">
                                    {m.attended.map((a, i) => (
                                        <span key={`${a.name}-${i}`} className="trc-idle-chip">
                                            {a.name}{a.isExternal ? ' (guest)' : ''} · {minutesText(a.durationSec)}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {record && <MeetingRecordModal record={record} onClose={() => setRecord(null)} />}
        </>
    );
};

export default TaskReportMeetings;
